import { useMemo } from "react";
import { object, number, bool, string, func, node } from "prop-types";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { BreakdownColumns } from "@repo/utils";
import { splitAndColorTextBySearchPhrases } from "@utils";
import {
  PinnableCell,
  useEstimateEditorSettings,
  useEstimateStructure,
  useEstimateTableSettings
} from "@tools";
import { BreakdownRowActions, TaskSelect, LibraryTaskNameEditor } from "./components";
import classnames from "classnames";

export const TaskTitleCell = observer(({
  children,
  columnSettings,
  element,
  actualDepthLevel,
  isSectionRow,
  expanded,
  tableDepth,
  handleDescriptionEdition,
  parentName,
  isLastPinnedCell,
}) => {
  const { t } = useTranslation();
  const structure = useEstimateStructure();
  const {
    allowEdition,
    displayLibraryElementStatus,
    allowLibraryElementStatusChange,
    isLibrary,
    isPdfPrinterView,
  } = useEstimateEditorSettings();
  const { useExpandCell } = useEstimateTableSettings();

  const { searchPhrases, estimateViewLevel } = structure;
  const { name, libraryStatus } = element;
  const { pinLeftPx } = columnSettings;
  
  const toAccept = useMemo(() => (
    displayLibraryElementStatus && allowLibraryElementStatusChange && libraryStatus === 0
  ), [
    libraryStatus, displayLibraryElementStatus, allowLibraryElementStatusChange
  ]);
  
  return (
    <PinnableCell
      resizeable keepMinSize
      boldText
      colName={ BreakdownColumns.TITLE }
      allowOverflowDisplay={isLastPinnedCell}
      pinnedLeft={typeof pinLeftPx === "number"}
      tableDepth={tableDepth}
      containers={children}
      actions={
        !isPdfPrinterView &&
        <BreakdownRowActions
          element={element}
          tableDepth={tableDepth}
          handleDescriptionEdition={handleDescriptionEdition}
          toAccept={toAccept}
          removable={allowEdition}
          hideBadge={expanded || estimateViewLevel === actualDepthLevel}
          expanded={expanded}
          useMenu
        />
      }
    >
      {
        isLibrary && (allowEdition || typeof element.id !== "number")
          ? <LibraryTaskNameEditor element={element} />
          : (
            allowEdition
              ? <TaskSelect
                element={element}
                tableDepth={actualDepthLevel}
                parentName={parentName}
              />
              : <span
                className={classnames(
                  "name wrap-text my-3",
                  !isSectionRow && "preset-titleText",
                  isSectionRow && "preset-titleTextSection",
                  !name?.length && "semi-transparent",
                  !useExpandCell && "ml-3"
                )}
              >
                {
                  splitAndColorTextBySearchPhrases(name, searchPhrases, t("common.unnamed"))
                }
              </span>
          )
      }
    </PinnableCell>
  )
});

TaskTitleCell.propTypes = {
  children: node,
  element: object.isRequired,
  columnSettings: object.isRequired,
  tableDepth: number.isRequired,
  expanded: bool,
  actualDepthLevel: number,
  isSectionRow: bool,
  handleDescriptionEdition: func.isRequired,
  parentName: string,
  isLastPinnedCell: bool,
};
